import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Plus, Search, Upload } from 'lucide-react'
import { getProducts } from '../../api/master'
import { listPickLocations } from '../../api/inventory'
import Spinner from '../../components/Spinner'
import ErrorBanner from '../../components/ErrorBanner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { useSession } from '../../session'
import { money } from '../sales/salesShared'
import { downloadCsv } from '../accounting/reportsShared'
import ItemBulkActions from './ItemBulkActions'
import { MasterTabs, discountText, isService, isTaxable, useMasterAccess } from './masterShared'

const PAGE_SIZE = 50
const SHOW = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'stock', label: 'Products', test: (i) => !isService(i) },
  { id: 'service', label: 'Services', test: isService },
  { id: 'untaxed', label: 'Not taxable', test: (i) => !isTaxable(i) },
  { id: 'discount', label: 'Discounted', test: (i) => !!discountText(i, money) },
  { id: 'noprice', label: 'No price', test: (i) => !(Number(i.unit_price) > 0) },
]
const SORTS = [
  { id: 'name', label: 'Name A–Z', cmp: (a, b) => String(a.name || '').localeCompare(String(b.name || '')) },
  { id: 'new', label: 'Newest first', cmp: (a, b) => Number(b.product_id) - Number(a.product_id) },
  { id: 'price', label: 'Highest price', cmp: (a, b) => Number(b.unit_price || 0) - Number(a.unit_price || 0) },
]
const cost = (i) => Number(i.layer_avg_cost ?? i.avg_cost ?? i.unit_cost ?? 0)

// Items: the products you stock and the services you sell, with price,
// cost, tax and discount. Click one to change it.
export default function ItemList() {
  const qc = useQueryClient()
  const { notify } = useToast()
  const { roles = [], binTracking: binsOn } = useSession()
  const can = useMasterAccess()
  const binTracking = binsOn && can.stock
  const canImport = ['admin', 'purchase', 'inventory'].some((r) => roles.includes(r))
  const [params, setParams] = useSearchParams()
  const show = SHOW.some((s) => s.id === params.get('show')) ? params.get('show') : 'all'
  const sort = SORTS.some((s) => s.id === params.get('sort')) ? params.get('sort') : 'name'
  const setParam = (key, value, fallback) => setParams((prev) => {
    const next = new URLSearchParams(prev)
    if (value === fallback) next.delete(key); else next.set(key, value)
    return next
  }, { replace: true })
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState(() => new Set())
  const [bins, setBins] = useState({})
  const allBox = useRef(null)

  const { data: items = [], isLoading, error } = useQuery({ queryKey: ['products'], queryFn: () => getProducts() })

  // Each stock item's main pick bin, when bins are tracked.
  useEffect(() => {
    if (!binTracking || !items.length) { setBins({}); return }
    listPickLocations(items.map((i) => i.product_id))
      .then((rows) => {
        const map = {}
        for (const r of rows || []) if (!map[r.product_id] || Number(r.is_primary) === 1) map[r.product_id] = r.path || ''
        setBins(map)
      })
      .catch(() => setBins({}))
  }, [items, binTracking])

  const counts = useMemo(() => Object.fromEntries(SHOW.map((s) => [s.id, items.filter(s.test).length])), [items])
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    const test = SHOW.find((s) => s.id === show).test
    return items
      .filter((i) => test(i) && (!term || [i.name, i.sku, i.barcode, i.description].join(' ').toLowerCase().includes(term)))
      .sort(SORTS.find((s) => s.id === sort).cmp)
  }, [items, search, show, sort])

  useEffect(() => { setPage(1) }, [search, show, sort])
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE))
  const pageRows = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const chosen = items.filter((i) => selected.has(i.product_id))
  const pageAll = pageRows.length > 0 && pageRows.every((i) => selected.has(i.product_id))
  const pageSome = pageRows.some((i) => selected.has(i.product_id))
  useEffect(() => { if (allBox.current) allBox.current.indeterminate = pageSome && !pageAll }, [pageSome, pageAll])

  const toggle = (id, on) => setSelected((prev) => { const next = new Set(prev); if (on) next.add(id); else next.delete(id); return next })
  const togglePage = (on) => setSelected((prev) => { const next = new Set(prev); pageRows.forEach((i) => (on ? next.add(i.product_id) : next.delete(i.product_id))); return next })

  const exportCsv = () => downloadCsv('products-and-services', [
    ['SKU', 'Name', 'Barcode', 'Type', 'Description', 'Selling price', 'Average cost', ...(can.tracked ? ['On hand'] : []), 'Taxable', 'Tax rate', 'Discount'],
    ...shown.map((i) => [i.sku, i.name, i.barcode, isService(i) ? 'Service' : 'Product', i.description, Number(i.unit_price || 0).toFixed(2),
      cost(i).toFixed(2), ...(can.tracked ? [isService(i) ? '' : Number(i.qty_on_hand || 0)] : []), isTaxable(i) ? 'Yes' : 'No', i.tax_rate ?? '', discountText(i, money)]),
  ])

  return (
    <div className="ci-page">
      <MasterTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Products &amp; services</h1>
            <div className="ci-title-meta">The products you stock and the services you sell.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          {canImport && <Link className="ci-btn ci-btn-ghost" to="/import"><Upload size={15} />Import</Link>}
          <button type="button" className="ci-btn ci-btn-ghost" onClick={exportCsv} disabled={!shown.length}><Download size={15} />Export</button>
          <Link className="ci-btn ci-btn-primary" to="/master/products/new"><Plus size={15} />New product or service</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search products and services">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, SKU or barcode…" />
        </label>
        <select className="ci-select" value={sort} onChange={(e) => setParam('sort', e.target.value, 'name')} aria-label="Sort">
          {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      <FilterChips options={SHOW.map((s) => ({ id: s.id, label: s.label, count: counts[s.id] }))} value={show} onChange={(id) => setParam('show', id, 'all')} />

      {error && <ErrorBanner message="Couldn’t load your products and services." />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th className="mf-check-col"><input ref={allBox} type="checkbox" checked={pageAll} onChange={(e) => togglePage(e.target.checked)} aria-label="Select all on this page" /></th>
                <th>Name</th>
                <th className="hide-sm">Type</th>
                {can.tracked && <th className="num hide-sm">On hand</th>}
                <th className="num hide-sm">Cost</th>
                <th className="num">Price</th>
                <th className="hide-sm">Discount</th>
                {binTracking && <th className="hide-sm">Pick bin</th>}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="8"><Spinner label="Loading products and services…" /></td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan="8"><div className="ci-empty">{items.length ? 'Nothing matches.' : <>No products or services yet. <Link to="/master/products/new">Add the first one</Link>.</>}</div></td></tr>
              ) : pageRows.map((i) => (
                <tr key={i.product_id} className={selected.has(i.product_id) ? 'is-selected' : ''}>
                  <td className="mf-check-col"><input type="checkbox" checked={selected.has(i.product_id)} onChange={(e) => toggle(i.product_id, e.target.checked)} aria-label={`Select ${i.name}`} /></td>
                  <td>
                    <Link className="ci-strong mf-row-link" to={`/master/products/${i.product_id}`}>{i.name || i.sku}</Link>
                    <span className="cell-sub">{[i.sku, i.barcode].filter(Boolean).join(' · ')}{!isTaxable(i) ? ' · not taxable' : ''}</span>
                  </td>
                  <td className="hide-sm">{isService(i) ? <StatusBadge label="Service" tone="info" /> : <span className="ci-muted-cell">Product</span>}</td>
                  {can.tracked && <td className="num hide-sm">{isService(i) ? '–' : Number(i.qty_on_hand || 0).toLocaleString()}</td>}
                  <td className="num hide-sm ci-muted-cell">{isService(i) && !cost(i) ? '–' : money(cost(i))}</td>
                  <td className="num ci-total">{money(i.unit_price)}</td>
                  <td className="hide-sm">{discountText(i, money) || <span className="ci-muted-cell">–</span>}</td>
                  {binTracking && <td className="hide-sm ci-muted-cell">{isService(i) ? '–' : (bins[i.product_id] || '–')}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} shown{pages > 1 ? ` · page ${page} of ${pages}` : ''}</span>
          {pages > 1 && (
            <div className="ci-page-btns">
              <button type="button" className="ci-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <button type="button" className="ci-page-btn" disabled={page === pages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </div>
      </div>

      <ItemBulkActions
        selected={chosen}
        binTracking={binTracking}
        onClear={() => setSelected(new Set())}
        onDone={(msg) => { notify(msg); qc.invalidateQueries({ queryKey: ['products'] }); setSelected(new Set()) }}
      />
    </div>
  )
}
